import React, { useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  FieldArray, FieldArrayPath, FieldPathByValue, FieldValues, Path, PathValue, useFormContext,
} from 'react-hook-form'
import { SupportingDocument } from 'features/declaration/models/schemas/documentSchemas'
import { FileType } from 'features/declaration/models/schemas/fileSchemas'
import { SUPPORTING_DOCUMENT_TYPE_CODE_PREFIX } from 'shared/ncts/common/classifier-util'
import useSoftDeleteFieldArray from 'shared/rhf/useSoftDeleteFieldArray'
import useCodelist from 'features/declaration/codelist/api/useCodelist'
import FormCollapsibleColumn from 'components/p5/FormCollapsibleColumn'
import FormField from 'components/p5/FormField'
import FormFieldArray from 'components/p5/FormFieldArray'
import FormSelect from 'components/p5/FormSelect'
import Input from 'components/p5/Input'
import DocumentFiles from 'features/declaration/document/components/DocumentFiles'
import DocumentDropdownArea from 'features/declaration/document/components/DocumentDropdownArea'
import dotLeafPaths from 'helpers/dot-leaf-paths'
import { ArrayPathByValue } from 'shared/rhf/models/types'
import { NctsScope } from 'shared/ncts/types'
import useDeclarationFieldRequirements
  from 'features/declaration/form/components/useDeclarationFieldRequirements'

const SUPPORTING_DOCUMENT_TYPE_PL_PREFIX = `${SUPPORTING_DOCUMENT_TYPE_CODE_PREFIX}PL_` as const

type FieldValuesWithSupportingDocuments = FieldValues & {
  supportingDocument: SupportingDocument[]
}

const scope = 'supportingDocument'

type SupportingDocumentRelPath = dotLeafPaths<SupportingDocument>

type SupportingDocumentBasePath<TForm extends FieldValuesWithSupportingDocuments> = Extract<Path<TForm>, `${typeof scope}.${number}`>
type SupportingDocumentFilesArrayPath<TForm extends FieldValuesWithSupportingDocuments> = Extract<Path<TForm>,
  `${typeof scope}.${number}.files`
>
type FileArrayPath<TForm extends FieldValues> = Extract<FieldArrayPath<TForm>, FieldPathByValue<TForm, FileType[]>>

type SupportingDocumentsArrayPath<TForm extends FieldValues> = ArrayPathByValue<TForm, SupportingDocument>

type SupportingDocumentsProps<TForm extends FieldValuesWithSupportingDocuments> = {
  scope: NctsScope
  createDefault: () => FieldArray<TForm, SupportingDocumentsArrayPath<TForm>>
  isEns: boolean
  country: string
}

function SupportingDocuments<
  TForm extends FieldValuesWithSupportingDocuments,
>({
  scope: formScope,
  createDefault,
  isEns,
  country,
}: Readonly<SupportingDocumentsProps<TForm>>) {
  const { t } = useTranslation()

  const { control, getValues, setValue } = useFormContext<TForm>()
  const {
    visibleFields,
    softRemove,
    appendWithSequence,
  } = useSoftDeleteFieldArray<TForm, SupportingDocument, SupportingDocumentsArrayPath<TForm>>({
    control,
    name: scope as SupportingDocumentsArrayPath<TForm>,
  })
  const [, allSupportingDocumentsOptions] = useCodelist('SUPPORTING_DOCUMENTS')
  const supportingDocumentsOptions = useMemo(
    () => (country === 'PL'
      ? allSupportingDocumentsOptions
      : allSupportingDocumentsOptions.filter((option) => !option.value.startsWith(SUPPORTING_DOCUMENT_TYPE_PL_PREFIX))),
    [allSupportingDocumentsOptions, country],
  )

  const removeDocument = useCallback((index: number) => {
    const innerItemScope = `${scope}.${index}` as SupportingDocumentBasePath<TForm>
    const filesPath = `${innerItemScope}.files` as SupportingDocumentFilesArrayPath<TForm>
    const filesOfRemovedDocument = getValues(filesPath)
    const updatedFiles = filesOfRemovedDocument.map((f) => (
      {
        ...f,
        deleted: true,
      }
    ))
    setValue(filesPath, updatedFiles as PathValue<TForm, typeof filesPath>)

    softRemove(index)
  }, [softRemove, getValues, setValue])

  useEffect(() => {
    if (country === 'PL') return
    visibleFields.forEach(({ field, index }) => {
      if (!field.documentType.startsWith(SUPPORTING_DOCUMENT_TYPE_PL_PREFIX)) return
      setValue(
        `${scope}.${index}.documentType` as Path<TForm>,
        '' as PathValue<TForm, Path<TForm>>,
        { shouldDirty: true },
      )
    })
  }, [country, visibleFields, setValue])

  const getValueAtIndex = (index: number, relPath: SupportingDocumentRelPath) => {
    const fullPath = `supportingDocument.${index}.${relPath}` as Path<TForm>

    return getValues(fullPath)
  }

  const paths = useMemo(
    () => [
      scope,
      ...visibleFields.map((
        f,
      ) => `${scope}.${f.index}.documentType`),
      ...visibleFields.map((
        f,
      ) => `${scope}.${f.index}.referenceNumber`),
    ],
    [visibleFields],
  )

  const fieldRequirements = useDeclarationFieldRequirements<TForm>(formScope, paths)
  const isAddForbidden = fieldRequirements.supportingDocument?.requirement === 'forbidden'

  return (
    <section className="py-3" key="supportingDocumentsSection">
      <FormCollapsibleColumn
        key={scope}
        columnId="declaration-supporting-documents"
        headingLabel={t('declaration.p5.supportingDocuments')}
        fieldRule={fieldRequirements.supportingDocument}
      >
        {visibleFields.map((fieldWithIndex) => {
          const supportingDocument = fieldWithIndex.field
          const innerItemScope = `${scope}.${fieldWithIndex.index}` as SupportingDocumentBasePath<TForm>
          const filesPath = `${innerItemScope}.files` as FileArrayPath<TForm>
          const documentId = getValueAtIndex(fieldWithIndex.index, 'id')

          return (
            <FormFieldArray
              key={`supportingDocument_${supportingDocument.id}`}
              titleKey="declaration.p5.sequenceNumber"
              removeItem={() => removeDocument(fieldWithIndex.index)}
              sequenceNumber={supportingDocument.sequenceNumber + 1}
              hidden={supportingDocument.deleted}
            >
              <div className="field-array-row">
                <FormField
                  labelKey="declaration.p5.supportingDocumentType"
                  fieldRule={fieldRequirements[`${innerItemScope}.documentType`]}
                >
                  <FormSelect
                    field={`${innerItemScope}.documentType`}
                    labelKey="declaration.p5.supportingDocumentType"
                    options={supportingDocumentsOptions}
                    type="sync"
                    fieldRule={fieldRequirements[`${innerItemScope}.documentType`]}
                  />
                </FormField>
                <FormField
                  labelKey="declaration.p5.supportingDocumentReferenceNumber"
                  fieldRule={fieldRequirements[`${innerItemScope}.referenceNumber`]}
                >
                  <Input
                    type="text"
                    field={`${innerItemScope}.referenceNumber`}
                    labelKey="declaration.p5.supportingDocumentReferenceNumber"
                    fieldRule={fieldRequirements[`${innerItemScope}.referenceNumber`]}
                  />
                </FormField>
                {
                  !isEns && (
                    <>
                      <FormField labelKey="declaration.p5.complementOfInformation">
                        <Input
                          type="text"
                          field={`${innerItemScope}.complementOfInformation`}
                          labelKey="declaration.p5.complementOfInformation"
                        />
                      </FormField>
                      <FormField labelKey="declaration.p5.itemNumber">
                        <Input
                          type="number"
                          min={0}
                          field={`${innerItemScope}.documentLineNumber`}
                          labelKey="declaration.p5.itemNumber"
                        />
                      </FormField>
                    </>
                  )
                }
              </div>
              <DocumentFiles<TForm>
                key={`documentFileNames${supportingDocument.id + supportingDocument.sequenceNumber}`}
                name={filesPath}
              />
              <DocumentDropdownArea
                key={`documents${supportingDocument.sequenceNumber + supportingDocument.id}`}
                name={filesPath}
                country={country}
                documentId={documentId}
              />
            </FormFieldArray>
          )
        })}
        <div className="row">
          <div className={`col-12 text-end ${isAddForbidden ? 'cursor--not-allowed' : ''}`}>
            <button
              type="button"
              className="btn btn-sm btn-primary mt-2"
              onClick={() => appendWithSequence(createDefault())}
              disabled={isAddForbidden}
            >
              <i className="fa fa-plus me-2" />
              {t('declaration.p5.addNewDocument')}
            </button>
          </div>
        </div>
      </FormCollapsibleColumn>
    </section>
  )
}

export default SupportingDocuments
